/**
 * Transform backend navigation registry to MenuPolicy[] (same shape as menu.config.generated).
 * Used at runtime so guideUrl and other registry data stay in sync without running generate:navigation.
 * No permission-based dropping — the API response is already PCS-filtered.
 */

import type { MenuPolicy } from '@/config/navigation/menu.config.generated.types'
import type { NavigationRegistryEntry } from '@/services/api/shared/navigation.service'

function slugify(name: string): string {
  if (!name || typeof name !== 'string') return 'item'
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'item'
}

export type Scope = 'platform' | 'organization' | 'branch'

const PLATFORM_CONFIG_LEGACY_ROUTES = new Set([
  '/platform/configuration/features',
  '/platform/configuration/settings',
  '/platform/app-settings',
])

const APP_SETTINGS_SHELL_PREFIX = '/platform/app-settings/'

function filterPlatformConfigurationErpChildren(children: MenuPolicy[]): MenuPolicy[] {
  return children.filter((child) => {
    const path = child.path ?? ''
    if (path.startsWith(APP_SETTINGS_SHELL_PREFIX)) return false
    if (PLATFORM_CONFIG_LEGACY_ROUTES.has(path)) return false
    return true
  })
}

function mapRegistryEntryToMenuPolicy(main: NavigationRegistryEntry, scope: Scope): MenuPolicy {
  const parentKey = slugify(main.mainMenuName ?? '')
  const parentPath = main.route ?? `/${scope}`
  const mappedChildren = (main.children ?? [])
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((child) => {
      const childKey = slugify(child.name ?? '') || `${parentKey}-child`
      const anyOf = child.anyOfPermissions?.filter((p) => typeof p === 'string' && p.trim()) ?? []
      const useAny = anyOf.length > 1
      const row: MenuPolicy = {
        key: childKey,
        label: child.name ?? childKey,
        path: child.route ?? parentPath,
        orderIndex: child.orderIndex,
        permission: useAny ? undefined : (child.permission?.trim() || anyOf[0] || undefined),
        anyOfPermissions: useAny ? anyOf : undefined,
        feature: child.feature ?? undefined,
        guideUrl: child.guideUrl ?? undefined,
        icon: child.icon ?? undefined,
      }
      if (child.navigationAudiences?.length) {
        row.navigationAudiences = child.navigationAudiences
      } else if (child.scopeType === 'admin' || child.scopeType === 'user') {
        row.scopeType = child.scopeType
      }
      return row
    })

  return {
    key: parentKey,
    label: main.mainMenuName ?? parentKey,
    path: parentPath,
    orderIndex: main.orderIndex,
    permission: undefined,
    feature: undefined,
    platformKey: main.platformKey ?? `${main.mainMenuName}:${scope}`,
    icon: main.icon ?? undefined,
    ...(main.navigationAudiences?.length ? { navigationAudiences: main.navigationAudiences } : {}),
    children: mappedChildren.length > 0 ? mappedChildren : undefined,
  }
}

export function registryToMenuPolicies(registry: NavigationRegistryEntry[], scope: Scope): MenuPolicy[] {
  const sorted = [...registry].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
  const policies = sorted.map((main) => mapRegistryEntryToMenuPolicy(main, scope))

  if (scope === 'platform') {
    return policies.map((main) => {
      if (main.key !== 'platform-configuration' || !main.children?.length) return main
      return {
        ...main,
        children: filterPlatformConfigurationErpChildren(main.children),
      }
    })
  }

  return policies
}
